import React from 'react'
import { render } from 'react-dom'
import { hydrateRoot } from 'react-dom/client'
import axios from 'axios'
import App from '../App'
import { BrowserRouter } from 'react-router-dom'
import '../components/ProjectNotes/projectNotes.css'
import { useBootstrapStore } from '../stores/useBootstrapStore'
import { useZustand } from '../hooks/useZustand'
import useBrowserTheme from '../hooks/useBrowserTheme'
import { usePaywallStore } from '../components/Paywall/usePaywallStore'
import { isPaywallCode } from '../components/Paywall/paywallCopy'

// Tag axios requests so the server can distinguish the main web app from the
// browser extension / Obsidian plugin / raw API clients (all of which share a
// `mozilla` User-Agent). axios calls are same-origin, so this never leaks to
// third-party hosts. Native fetch() calls aren't tagged but are still correctly
// classified server-side via the same-origin Origin/Referer check.
// See lib/pass.js -> getAnonymousApplicationType.
if (typeof window !== 'undefined') {
    axios.defaults.headers.common['X-Infranodus-Client'] = 'web-app'

    // Carry the user's original QuickStart query (captured in sessionStorage as
    // `firstSearch`) to the server on the import request that actually submits
    // it, so the import tracking log can record what was first typed vs. what
    // was finally submitted. Consume-once: it is removed as soon as it is
    // attached, so it is only ever saved alongside a real import submission and
    // never leaks onto a later, unrelated import. Same-origin only; encoded so
    // it is header-safe.
    axios.interceptors.request.use(requestConfig => {
        try {
            const url = requestConfig.url || ''
            // `/import/*` covers the regular importers; `/openai` covers the
            // AI-graph importers (AiTopics/AiGraph/AiOntologies) that POST to
            // /graph/:user/openai.
            if (url.includes('/import') || url.includes('/openai')) {
                const firstSearch = window.sessionStorage.getItem('firstSearch')
                if (firstSearch) {
                    requestConfig.headers = requestConfig.headers || {}
                    requestConfig.headers[
                        'X-First-Search'
                    ] = encodeURIComponent(firstSearch.slice(0, 500))
                    window.sessionStorage.removeItem('firstSearch')
                }
            }
        } catch (err) {
            // sessionStorage may be unavailable (private mode); ignore.
        }
        return requestConfig
    })

    // A typed 402 (QUOTA_* / FEATURE_PAID_ONLY, see lib/uploadLimits.js) is a
    // paywall moment: open the upgrade dialog with the server's reason and
    // still reject, so the caller's own error handling keeps working.
    axios.interceptors.response.use(
        response => response,
        error => {
            const response = error && error.response
            const body = response && response.data
            if (response?.status === 402 && body && isPaywallCode(body.code)) {
                usePaywallStore.getState().openUpgradeDialog(body)
            }
            return Promise.reject(error)
        }
    )
}

// Dev-mode store exposure for DevTools verification. Not present in
// production builds. Lets us paste `useBootstrapStore.getState()` etc.
// in the console without React DevTools.
// Dev-only handles for poking the stores from the browser console; nothing in
// the app reads them.
if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
    window.useBootstrapStore = useBootstrapStore
    window.useZustand = useZustand
    window.useBrowserTheme = useBrowserTheme
    window.usePaywallStore = usePaywallStore
}

let mountElement = document.getElementById('react-root')
const propsData = mountElement?.getAttribute('data-props')

const initialProps = propsData ? JSON.parse(propsData) : {}

let reactMountFn = mountElement?.childElementCount === 0 ? render : hydrateRoot

mountElement &&
    reactMountFn(
        mountElement,
        <BrowserRouter>
            <App {...initialProps} />
        </BrowserRouter>
    )
